/**
 * TUVI - Componente PDF de Cotizacion Vieja (importada)
 * Copia de CotizacionPDF sin secciones de cocina ni plano
 */

import React, { forwardRef } from 'react';

// Formatear moneda colombiana
const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Opciones de bano (mismas del componente principal)
const OPCIONES_BANO = [
  { id: 'conserva_todos', label: 'Se conservan todos los elementos entregados por la constructora en el area del bano' },
  { id: 'conserva_enchape', label: 'Se conserva enchape de piso y paredes' },
  { id: 'conserva_sanitario', label: 'Se conserva sanitario' },
  { id: 'cambia_puerta', label: 'Se cambia puerta de bano' },
  { id: 'conserva_lavamanos_griferia', label: 'Se conserva lavamanos y griferia' },
  { id: 'cambia_enchape_completo', label: 'Se cambia enchape completo' },
  { id: 'conserva_regadera', label: 'Se conserva regadera y mezclador' },
  { id: 'cambia_lavamanos', label: 'Se cambia solo lavamanos' },
  { id: 'conserva_accesorios', label: 'Se conserva Kit de accesorios ceramicos' },
  { id: 'renueva_completo', label: 'Se renueva bano completamente (Aparatos sanitarios, griferias y enchapes)' }
];

// Observaciones fijas
const OBSERVACIONES = [
  'Los precios de la presente propuesta tienen una validez de 30 dias calendario a partir de la fecha de expedicion.',
  'Los valores presentados incluyen Administracion, Imprevistos y Utilidad (AIU) e IVA.',
  'El plazo de entrega comenzara a correr una vez se firme el contrato de obra y se realice el primer desembolso acordado.',
  'Los acabados seran de primera calidad, utilizando marcas reconocidas del mercado nacional.',
  'Las actividades no contempladas en la presente propuesta seran cotizadas como adicionales previo acuerdo con el propietario.',
  'TUVI garantiza todos los trabajos realizados por un periodo de un (1) ano contado a partir de la fecha de entrega del inmueble.'
];

// Consideraciones especiales fijas
const CONSIDERACIONES = [
  'Cualquier modificacion estructural que se requiera debera contar con la aprobacion previa de la constructora del edificio.',
  'Los horarios de trabajo seran acordados con la administracion del conjunto o edificio, respetando los reglamentos de propiedad horizontal.',
  'El material existente que sea retirado durante la remodelacion sera de propiedad del propietario, salvo indicacion contraria por escrito.'
];

const CotizacionViejaPDF = forwardRef(({ cotizacion }, ref) => {
  if (!cotizacion) return null;

  const {
    consecutivo, fecha, nombre_cliente, telefono_cliente, correo_cliente,
    proyecto_nombre, apartamento, area_m2, habitaciones, banos,
    subtotal, aiu_porcentaje, aiu_valor, iva_valor, valor_final,
    actividades
  } = cotizacion;

  // Agrupar actividades por categoría
  const actividadesPorCategoria = {};
  (actividades || []).forEach(act => {
    const cat = act.categoria || 'GENERALES';
    if (!actividadesPorCategoria[cat]) {
      actividadesPorCategoria[cat] = [];
    }
    actividadesPorCategoria[cat].push(act);
  });

  const categoriasOrdenadas = Object.keys(actividadesPorCategoria);

  // Numeracion global de items
  let itemGlobal = 0;

  // Último día del mes actual en formato dd-mm-aaaa
  const hoy = new Date();
  const ultimoDiaMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
  const validezOferta = `${String(ultimoDiaMes.getDate()).padStart(2, '0')}-${String(ultimoDiaMes.getMonth() + 1).padStart(2, '0')}-${ultimoDiaMes.getFullYear()}`;

  return (
    <div ref={ref}>
      <div className="cotizacion-page">
        {/* Banner */}
        <div className="pdf-banner">
          <img src="/logo_tuvi_banner.jpg" alt="TUVI" className="pdf-banner-logo" crossOrigin="anonymous" />
          <span className="pdf-banner-title">COTIZACIÓN</span>
        </div>

        {/* Contenido */}
        <div style={{ padding: '65px 65px 0' }}>
          {/* Fecha de Cotizacion */}
          <div className="pdf-fecha-cotizacion">
            <span className="pdf-fecha-lbl">Fecha de Cotizacion</span>
            <span className="pdf-fecha-val">{fecha}</span>
          </div>

          {/* Datos del Propietario e Inmueble - 2 columnas */}
          <div className="pdf-datos-wrapper">
            {/* Columna izquierda: Propietario */}
            <div className="pdf-datos-left">
              <table className="pdf-datos-block">
                <thead>
                  <tr>
                    <th colSpan="2" className="pdf-datos-title-dark">Datos del Propietario</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="pdf-dato-lbl">Nombre</td>
                    <td className="pdf-dato-val">{nombre_cliente}</td>
                  </tr>
                  <tr>
                    <td className="pdf-dato-lbl">Telefono</td>
                    <td className="pdf-dato-val">{telefono_cliente}</td>
                  </tr>
                  <tr>
                    <td className="pdf-dato-lbl">Correo</td>
                    <td className="pdf-dato-val">{correo_cliente}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Columna derecha: Inmueble */}
            <div className="pdf-datos-right">
              <table className="pdf-datos-block">
                <thead>
                  <tr>
                    <th colSpan="4" className="pdf-datos-title-dark">Datos del Inmueble</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="pdf-dato-lbl">Proyecto</td>
                    <td colSpan="3" className="pdf-dato-val">{proyecto_nombre}</td>
                  </tr>
                  <tr className="pdf-separator-row"><td colSpan="4"></td></tr>
                  <tr>
                    <td className="pdf-dato-lbl">Area</td>
                    <td className="pdf-dato-val">{area_m2}</td>
                    <td className="pdf-dato-lbl">Habitaciones</td>
                    <td className="pdf-dato-val">{habitaciones}</td>
                  </tr>
                  <tr>
                    <td className="pdf-dato-lbl">Baños</td>
                    <td className="pdf-dato-val">{banos}</td>
                    <td className="pdf-dato-lbl">Apartamento</td>
                    <td className="pdf-dato-val">{apartamento}</td>
                  </tr>
                  <tr className="pdf-separator-row"><td colSpan="4"></td></tr>
                  <tr>
                    <td className="pdf-dato-lbl" colSpan="3">Validez de la oferta</td>
                    <td className="pdf-dato-val pdf-val-green">{validezOferta}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Bano que entrega la constructora */}
          <div className="cotizacion-pdf-section" style={{ marginTop: '6px' }}>
            <h4 className="pdf-section-beige">Bano que entrega la constructora</h4>
            <div className="pdf-bano-grid">
              {OPCIONES_BANO.map(opcion => (
                <div key={opcion.id} className="pdf-bano-item">
                  <span className="pdf-check-box">
                  </span>
                  <span className="pdf-bano-text">{opcion.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ACTIVIDADES DE REMODELACION */}
          <div className="cotizacion-pdf-section" style={{ marginTop: '10px', marginBottom: '5px' }}>
            <h4 className="pdf-section-beige">ACTIVIDADES DE REMODELACION</h4>
          </div>
          <table className="pdf-actividades-table">
            <thead>
              <tr>
                <th style={{ width: '30px' }}>Item</th>
                <th>Descripcion</th>
                <th style={{ width: '35px' }}>Und</th>
                <th style={{ width: '35px' }}>Cant</th>
                <th style={{ width: '80px' }} className="text-right">V. Total</th>
              </tr>
            </thead>
            <tbody>
              {categoriasOrdenadas.map((categoria, gIdx) => {
                const acts = actividadesPorCategoria[categoria] || [];
                const rows = [];
                const totalCategoria = acts.reduce((sum, act) => sum + (parseFloat(act.valor_total) || 0), 0);
                // 2 filas en blanco entre categorías
                if (gIdx > 0) {
                  rows.push(
                    <tr key={`sep1-${gIdx}`} className="pdf-espacio-separator"><td colSpan="5"></td></tr>,
                    <tr key={`sep2-${gIdx}`} className="pdf-espacio-separator"><td colSpan="5"></td></tr>
                  );
                }
                rows.push(
                  <tr key={`cat-${gIdx}`} className="espacio-header">
                    <td colSpan="4">{categoria}</td>
                    <td className="text-right">{formatCurrency(totalCategoria)}</td>
                  </tr>
                );
                acts.forEach((act, aIdx) => {
                  itemGlobal++;
                  rows.push(
                    <tr key={`act-${gIdx}-${aIdx}`} className={aIdx % 2 === 1 ? 'pdf-act-row-beige' : ''}>
                      <td>{itemGlobal}</td>
                      <td>{act.descripcion}</td>
                      <td>{act.unidad}</td>
                      <td>{(act.unidad === 'UND' || act.unidad === 'ML') ? act.cantidad : ''}</td>
                      <td className="text-right">{formatCurrency(act.valor_total)}</td>
                    </tr>
                  );
                });
                return rows;
              })}
              <tr className="subtotal-row">
                <td colSpan="4" className="text-right">SUBTOTAL</td>
                <td className="text-right">{formatCurrency(subtotal)}</td>
              </tr>
            </tbody>
          </table>

          {/* Resumen financiero */}
          <div className="cotizacion-pdf-section" style={{ marginTop: '15px' }}>
            <h4 className="pdf-section-beige">RESUMEN FINANCIERO</h4>
            <table className="pdf-financial-table">
              <tbody>
                <tr>
                  <td>Subtotal</td>
                  <td className="text-right">{formatCurrency(subtotal)}</td>
                </tr>
                <tr>
                  <td>AIU ({aiu_porcentaje || 15}%)</td>
                  <td className="text-right">{formatCurrency(aiu_valor)}</td>
                </tr>
                <tr>
                  <td>IVA</td>
                  <td className="text-right">{formatCurrency(iva_valor)}</td>
                </tr>
                <tr className="valor-final-row">
                  <td>VALOR FINAL DEL PROYECTO</td>
                  <td className="text-right">{formatCurrency(valor_final)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Observaciones */}
          <div className="cotizacion-pdf-section pdf-section-small" style={{ marginTop: '10px' }}>
            <h4 className="pdf-section-beige">OBSERVACIONES</h4>
            <div className="pdf-observaciones">
              {OBSERVACIONES.map((obs, idx) => (
                <p key={idx}>&#8226; {obs}</p>
              ))}
            </div>
          </div>

          {/* Consideraciones especiales */}
          <div className="cotizacion-pdf-section pdf-section-small" style={{ marginTop: '10px' }}>
            <h4 className="pdf-section-beige">CONSIDERACIONES ESPECIALES</h4>
            <div className="pdf-consideraciones">
              {CONSIDERACIONES.map((con, idx) => (
                <p key={idx}>&#8226; {con}</p>
              ))}
            </div>
          </div>

        </div>

        {/* Imagen final */}
        <div className="pdf-imagen-final">
          <img src="/final_cotizador.png" alt="TUVI" crossOrigin="anonymous" />
        </div>
      </div>
    </div>
  );
});

CotizacionViejaPDF.displayName = 'CotizacionViejaPDF';

export default CotizacionViejaPDF;
